import { Box, Grid, Paper, Typography } from '@mui/material'
import type { Payment } from '../generated/graphql'
import { formatCurrency, formatStatus, formatTimestamp } from '../utils/util'

interface PaymentSummaryProps {
  payment: Payment
}

export default function PaymentSummary({ payment }: PaymentSummaryProps) {
  const {
    amount,
    createdAt,
    payeeId,
    status,
    updatedAt,
    paymentOption,
    isBankError,
    failureDetail,
    paymentErrorMessage,
  } = payment
  const failedGridSize = status === 'FAILED' ? 1 : 2

  return (
    <Paper sx={{ padding: '2em 1em 0', marginBottom: 2 }}>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: failedGridSize }}>
          <KeyValuePair label="Amount" val={formatCurrency(amount as number)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: failedGridSize }}>
          <KeyValuePair label="Status" val={formatStatus(status)} />
        </Grid>
        {status === 'FAILED' && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <KeyValuePair
              label="Failure Type"
              val={isBankError === true ? 'Bank Error' : isBankError === false ? 'API Failure' : 'Unknown'}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KeyValuePair label="Payee ID" val={payeeId as string} />
        </Grid>
        {!['OPEN', 'UNDER_REVIEW'].includes(status as string) && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <KeyValuePair label="Payment Speed" val={paymentOption as string} />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KeyValuePair label="Created" val={formatTimestamp(createdAt)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KeyValuePair label="Updated" val={formatTimestamp(updatedAt)} />
        </Grid>
      </Grid>
      {(paymentErrorMessage || failureDetail) && (
        <Box sx={{ pb: 0.5, borderColor: 'error.main', borderRadius: 1 }}>
          <Typography fontWeight="bold" gutterBottom>
            Payment Error
          </Typography>
          {paymentErrorMessage && !failureDetail?.description && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Error Message
              </Typography>
              <Typography variant="body2">{paymentErrorMessage}</Typography>
            </>
          )}
          {failureDetail?.type && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Error Type
              </Typography>
              <Typography variant="body2">{failureDetail.type}</Typography>
            </>
          )}
          {failureDetail?.description && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Error Description
              </Typography>
              <Typography variant="body2">{failureDetail.description}</Typography>
            </>
          )}
        </Box>
      )}
    </Paper>
  )
}

interface KeyValuePairProps {
  label: string
  val: string | number | undefined
}

function KeyValuePair({ label, val }: KeyValuePairProps) {
  return (
    <div>
      <Typography gutterBottom variant="body1" component="h2" sx={{ fontWeight: 900 }}>
        {label}
      </Typography>
      <Typography variant="body1" color="textSecondary" component="p">
        {val || '-'}
      </Typography>
      <br />
    </div>
  )
}
